/* C4 — Apippa presentation layer for product.c4-legacy (and the cart surfaces its lines land on).
 *
 * Apippa is the legacy pricing engine and stays in charge of every number. This file only makes
 * its output read like the rest of the C4 design, and cleans up what its carrier variants leave
 * in the cart. It never computes, rewrites or posts a price.
 *
 *   1. PRICE. The C4 price block shows the protected variant's "From $X" until the customer
 *      configures. Apippa keeps its own live total in #calculator-final-value / #priceTicker
 *      (the same elements Dawn's buy-buttons watched). We mirror that text across, so the page
 *      does not show "From $60.00" while the calculator says $140.
 *   1b. CALCULATOR RESTORE on the cart -> "Edit design" round trip. See the block below.
 *   2. CART TITLE. Apippa swaps the variant at add time for one of its Default_cpc_* carriers,
 *      so the cart would otherwise show "Default_cpc_EC003C23A2174" as the customer's choice.
 *      Dawn hid those option values; Horizon renders variant titles in a different component, so
 *      they are hidden here by text match. The real configuration is already on the line as
 *      Apippa's own properties (Size, Width, Height, Quantity …).
 *   3. CART QUANTITY. An Apippa line's price IS the job total for the quantity chosen in the
 *      calculator, so a second quantity control in the cart means two different quantities on one
 *      line. Production removed it; we hide it the same way and leave Remove. Nothing is put in
 *      its place: an earlier build wrote "Qty N · priced by the calculator" there, which Cathy
 *      read as a note to ourselves rather than anything a customer needs (14 Sep 2026). The
 *      quantity is already on the line as Apippa's own Quantity property.
 *
 * Scope: only pages that carry a legacy product form, and only cart lines that carry Apippa's
 * _cpc_custom_cart_item property. Nothing here runs on the online template.
 *
 * ---------------------------------------------------------------------------------------------
 * THE /cart.js REQUEST STORM, AND WHY THE GUARDS BELOW ARE THE WAY THEY ARE (14 Sep 2026)
 *
 * Measured on an IDLE product page: `GET /cart.js` was firing about 1.2 times per SECOND -- some
 * 70 requests a minute, per open tab, forever. Nearly 20,000 requests and 17,600 console messages
 * had rolled out of the browser's buffers. Cathy hit "An error occurred during the request: error"
 * on add to cart and had never seen it before; two 400s were in the network log. Shopify rate
 * limits /cart.js, so a storm like this makes an add-to-cart fail intermittently and for no
 * reason the customer can see or act on.
 *
 * It was this file. Three mistakes compounding:
 *
 *   a) `run()` -- which ALWAYS fetched /cart.js -- was wired straight to a MutationObserver on
 *      document.body with subtree:true, and no debounce. Every DOM change anywhere on the page
 *      bought one cart fetch. A product page is never still: Apippa repaints on every option,
 *      the template carousel clones slides, Judge.me and Sami inject, and the price poller in
 *      this very file rewrites text on a timer.
 *
 *   b) The `mo._busy` + `takeRecords()` guard LOOKED like it prevented feedback, and does not.
 *      `lockQuantities()` is asynchronous: it mutates the DOM inside `.then()`, long after the
 *      synchronous block has set `_busy` back to false and drained the queue. A guard around a
 *      promise's *start* protects nothing at its *finish*.
 *
 *   c) `onCart` matches `cart-drawer-component` / `[id*="cart-drawer"]`, and the drawer is in the
 *      LAYOUT -- so it exists on every page of the site, empty. `onCart` was therefore true
 *      everywhere, and this ran on the homepage too.
 *
 * The fixes, in the order that matters:
 *   1. NEVER FETCH WITHOUT LINES TO ACT ON. `lockQuantities()` returns before the fetch unless a
 *      cart container actually holds line items. On a product page with a closed empty drawer,
 *      that is zero requests instead of seventy a minute. This one change is what stops it.
 *   2. DEBOUNCE. Mutations coalesce into a single trailing run, so a burst of fifty DOM changes
 *      costs one pass rather than fifty.
 *   3. A FLOOR BETWEEN FETCHES. Even when the cart is on screen, two cart reads cannot be closer
 *      together than MIN_FETCH_MS. Belt and braces: if a future change reintroduces a feedback
 *      path, it degrades to one request every couple of seconds instead of a storm.
 *
 * If you add anything to `run()`, keep it cheap and idempotent, and do not put a network call in
 * it that is not behind the same has-lines test. The calculator restore below adds the only other
 * /cart.js read in this file: exactly one, and only when ?pp_project= is on the URL.
 * ---------------------------------------------------------------------------------------------
 */
(function () {
  /* Run once: the legacy section and the layout's cart drawer both request this file, and two
     copies would mean two price pollers and a doubled cart pass. */
  if (window.__c4Legacy) return;
  window.__c4Legacy = true;

  var form = document.querySelector('form[data-c4-legacy]');

  /* Read before anything else: c4-pitchprint.js strips pp_project from the URL once it has
     restored the project, and on that path the page also reloads. */
  var EDIT_PROJECT = '';
  var EDIT_LINE = '';
  try {
    var editQuery = new URLSearchParams(location.search);
    EDIT_PROJECT = editQuery.get('pp_project') || '';
    EDIT_LINE = editQuery.get('pp_line') || '';
  } catch (e) {}

  /* ---- 0. hold the calculator's place until Apippa lands ----------------------------------- */
  if (form) {
    var skel = document.querySelector('[data-c4-calc-skeleton]');
    if (skel) {
      var drop = function () {
        var calc = document.getElementById('calculator');
        if (!calc || !calc.children.length) return false;
        skel.remove();
        return true;
      };
      if (!drop()) {
        var skelTries = 0;
        var skelTimer = setInterval(function () {
          /* ~40s of patience: Apippa's chain is serial and slow, and a stranded skeleton is worse
             than a late one -- so give up eventually rather than leave it on screen forever. */
          if (drop() || ++skelTries > 200) {
            clearInterval(skelTimer);
            if (skelTries > 200 && skel.parentNode) skel.remove();
          }
        }, 200);
      }
    }
  }

  /* ---- 1. price mirror (product page only) ------------------------------------------------ */
  if (form) {
    var cell = document.querySelector('.c4-price [data-c4-price]');
    var note = document.querySelector('.c4-price .c4-price__n');
    var kicker = document.querySelector('.c4-price .c4-price__k');
    var idleKicker = kicker && kicker.textContent;
    var idleNote = note && note.textContent;

    var money = function (s) {
      var m = String(s || '').replace(/\s+/g, ' ').match(/\$\s?([\d,]+(?:\.\d{1,2})?)/);
      if (!m) return null;
      var n = parseFloat(m[1].replace(/,/g, ''));
      if (!isFinite(n) || n <= 0) return null;
      return '$' + n.toFixed(2);
    };

    var apippaPrice = function () {
      /* Apippa writes the final figure to #priceTicker[data-origMoney] the instant it computes,
         then ANIMATES the visible text to it over the next few hundred ms. Reading the attribute
         first keeps the panel and button in step with the "You save" readout, which is derived
         from that same attribute (15 Sep 2026). Text is the fallback. */
      var ticker = document.querySelector('#priceTicker');
      if (ticker) {
        var raw = parseFloat(ticker.getAttribute('data-origMoney') || ticker.getAttribute('data-origmoney'));
        if (isFinite(raw) && raw > 0) return '$' + raw.toFixed(2);
      }
      var els = [document.querySelector('#calculator-final-value'), document.querySelector('#priceTicker')];
      for (var i = 0; i < els.length; i++) {
        if (!els[i]) continue;
        var v = money(els[i].textContent);
        if (v) return v;
      }
      /* Fallback: the price marker the calculator paints under its fields. */
      var marker = document.querySelector('#calculator .price-marker');
      return marker ? money(marker.textContent) : null;
    };

    /* ---- 1a. no size, no order (Cathy, 15 Sep 2026: "custom pricing where w=0 and h=0") ----
       Corflute, ACP, vinyl and the like have no size presets -- only Width (mm) / Height (mm)
       boxes, which Apippa opens at 0 x 0 and prices at the calculator's minimum ($80). The seed
       below (1a2) only helps products that also have a Size dropdown, so on these the page
       happily sold a 0 x 0 sign at the minimum. Now: while either box is blank or 0 the price
       panel reads "From $80 -- enter your size", the Add to cart button is disabled with a line
       under it, and a form submit (Enter in a field) is swallowed before Apippa sees it. The
       moment both boxes hold a number everything comes back. Nothing is written into the
       calculator: no invented size, no changed price. */
    var sizeIncomplete = function () {
      var calc = document.getElementById('calculator');
      if (!calc) return false;
      var w = calc.querySelector('input[name="properties[Width (mm)]"]');
      var h = calc.querySelector('input[name="properties[Height (mm)]"]');
      if (!w || !h) return false;
      var vis = function (el) { return el.offsetWidth || el.offsetHeight || el.getClientRects().length; };
      if (!vis(w) && !vis(h)) return false;
      return !((parseFloat(w.value) || 0) > 0 && (parseFloat(h.value) || 0) > 0);
    };
    var sizeGuardOn = false;
    var applySizeGuard = function () {
      var on = sizeIncomplete();
      var btn = document.querySelector('[data-c4-cta]');
      if (btn) {
        var wasOn = btn.hasAttribute('data-c4-size-wait');
        if (on && !wasOn) {
          btn.setAttribute('data-c4-size-wait', '');
          btn.disabled = true;
          btn.setAttribute('aria-disabled', 'true');
          var noteEl = document.createElement('p');
          noteEl.setAttribute('data-c4-size-note', '');
          noteEl.style.cssText = 'margin:6px 0 0;text-align:center;font-size:12px;color:#c0392b;font-weight:600';
          noteEl.textContent = 'Enter your width and height (mm) to see your price';
          btn.insertAdjacentElement('afterend', noteEl);
        } else if (!on && wasOn) {
          btn.removeAttribute('data-c4-size-wait');
          btn.disabled = false;
          btn.removeAttribute('aria-disabled');
          var old = document.querySelector('[data-c4-size-note]');
          if (old) old.remove();
        }
      }
      sizeGuardOn = on;
      return on;
    };
    document.addEventListener('submit', function (e) {
      if (!e.target || !e.target.matches || !e.target.matches('form[data-c4-legacy]')) return;
      if (!sizeIncomplete()) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      var w = document.querySelector('#calculator input[name="properties[Width (mm)]"]');
      if (w) { try { w.focus(); } catch (err) {} }
    }, true);
    /* Apippa's own click handler on the button runs before the submit; a disabled button never
       clicks, but a click arriving through the sticky bar proxy still has to be refused. */
    document.addEventListener('click', function (e) {
      var t = e.target && e.target.closest && e.target.closest('[data-c4-cta], [data-c4-sticky-btn]');
      if (!t || !sizeIncomplete()) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      var w = document.querySelector('#calculator input[name="properties[Width (mm)]"]');
      if (w) { try { w.focus(); w.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (err) {} }
    }, true);

    var syncPrice = function () {
      var v = apippaPrice();
      if (!v) return;
      var waiting = applySizeGuard();
      if (cell) {
        var shown = waiting ? ('From ' + v) : v;
        if (cell.textContent !== shown) cell.textContent = shown;
        var k = waiting ? 'Enter your size' : 'Your price';
        var n = waiting ? 'inc GST · price updates as you type' : 'inc GST · as configured';
        if (kicker && kicker.textContent !== k) kicker.textContent = k;
        if (note && note.textContent !== n) note.textContent = n;
      }
      /* The mobile sticky bar mirrors this same cell -- see syncSticky() in c4-print-app.js. */
    };

    /* Apippa repaints on every option change; a slow poll that backs off is enough and avoids a
       body-wide observer. Reverts to the "From" wording if the calculator is ever cleared. */
    var wait = 500, calm = 0, last = '';
    /* A change in the calculator wakes the poll at once: once calm, the poll had backed off to 5s,
       so the price panel (and the button that mirrors it) could sit on the old figure for up to
       five seconds after an option changed -- easy to miss before, obvious next to the "You save"
       readout that answers instantly (15 Sep 2026). */
    ['change', 'input'].forEach(function (evt) {
      document.addEventListener(evt, function (e) {
        if (!e.target || !e.target.closest || !e.target.closest('#calculator')) return;
        wait = 500; calm = 0;
        setTimeout(syncPrice, 350);
        setTimeout(syncPrice, 900);
      }, true);
    });
    (function tick() {
      setTimeout(function () {
        if (!document.hidden) {
          var v = apippaPrice();
          if (v) syncPrice();
          else if (cell && kicker && idleKicker) { kicker.textContent = idleKicker; if (note && idleNote) note.textContent = idleNote; }
          if (v === last) { calm++; if (calm >= 4) wait = Math.min(wait * 2, 5000); }
          else { last = v; calm = 0; wait = 500; }
        }
        tick();
      }, wait);
    })();
  }

  /* ---- 1a2. a blank custom size is not a real order ---------------------------------------
     Cathy, 14 Sep 2026. The calculator lands on "Custom size" with width and height both 0. Two
     things follow from that, and both are bad: the line can be added to the cart exactly as it is,
     producing an order that reads Width 0 / Height 0 at the base price; and the template carousel
     has no size to filter on, so the customer is shown almost no designs ("2 designs").

     So on a fresh arrival with both dimensions blank, seed them from the CHEAPEST pre-determined
     size -- smallest area, since these price by the square metre -- so the page opens at the least
     the customer could pay rather than at whatever sits at the top of the list.

     The dropdown then snaps to that preset, and that is correct rather than a bug: the theme's own
     size-sync matches the width and height back to a matching option, and a dropdown reading
     "Custom size" above fields saying 841 x 594 would be the lie. "Custom size" stays in the list
     and stays selectable, which is what Cathy asked for. Longer term she wants the dropdown replaced
     with something that shows every option at once.

     Never on the way back from the cart: a line being edited brings its own values, and seeding
     would fight the restore. Never once the customer has typed a dimension either -- the check is
     "both are still zero", not "the page just loaded".

     Consequence worth knowing: the price on arrival is now the cheapest preset's price, not the
     base "from" price. That is the honest number for the size actually sitting in the fields.
  ------------------------------------------------------------------------------------------- */
  if (form && !EDIT_PROJECT && !EDIT_LINE) {
    var seedDone = false;
    var seedTries = 0;
    var seedTimer = setInterval(function () {
      if (seedDone || ++seedTries > 60) { clearInterval(seedTimer); return; }
      var calc = document.getElementById('calculator');
      if (!calc) return;
      var wEl = calc.querySelector('input[name="properties[Width (mm)]"]');
      var hEl = calc.querySelector('input[name="properties[Height (mm)]"]');
      var sizeEl = calc.querySelector('select[name="properties[Size]"]');
      if (!wEl || !hEl || !sizeEl) return;

      /* Anything already entered -- by the customer or by Apippa -- and we leave well alone. */
      if ((parseFloat(wEl.value) || 0) > 0 || (parseFloat(hEl.value) || 0) > 0) {
        seedDone = true;
        clearInterval(seedTimer);
        return;
      }

      /* The SMALLEST preset, not the first one in the list. Seeding a size seeds a price, so the
         page should open at the least the customer could pay rather than at whatever happens to sit
         at the top of the dropdown (Cathy, 14 Sep 2026). Area is the proxy for cheapest: these
         products are priced by the square metre, so the smallest area is the cheapest start. If a
         product is ever priced some other way this picks a sensible small default rather than a
         correct one -- revisit it there rather than here. */
      var dims = null;
      var bestArea = 0;
      for (var i = 0; i < sizeEl.options.length; i++) {
        var m = (sizeEl.options[i].value || '').match(/(\d+(?:\.\d+)?)\s*[xX]\s*(\d+(?:\.\d+)?)/);
        if (!m) continue;
        var area = parseFloat(m[1]) * parseFloat(m[2]);
        if (!isFinite(area) || area <= 0) continue;
        if (!dims || area < bestArea) { dims = m; bestArea = area; }
      }
      seedDone = true;
      clearInterval(seedTimer);
      /* No preset to borrow from: leave it blank rather than invent a size. */
      if (!dims) return;

      wEl.value = dims[1];
      hEl.value = dims[2];
      /* Flag it BEFORE the events fire: the design carousel listens for these and must already know
         this size is ours, not the customer's. It filters designs by the chosen size, and a seeded
         size the customer never picked would silently change which templates they are shown --
         Banners' designs are tagged "Custom Size" / "2m x 1m", which the cheapest preset (A1) does
         not match (Cathy, 14 Sep 2026). Cleared by the listener below the moment they touch
         anything in the calculator themselves. */
      var sizeWas = sizeEl.value;
      document.documentElement.setAttribute('data-c4-size-seeded', '');
      wEl.dispatchEvent(new Event('input', { bubbles: true }));
      wEl.dispatchEvent(new Event('change', { bubbles: true }));
      hEl.dispatchEvent(new Event('input', { bubbles: true }));
      hEl.dispatchEvent(new Event('change', { bubbles: true }));

      /* THE DROPDOWN GOES BACK TO "Custom size", which is what Cathy asked for on 14 Sep and what
         the rest of the page depends on. The theme's size-sync watches those input events and,
         50ms later, snaps the dropdown to whichever preset matches the dimensions. That looked
         harmless -- the dropdown agreeing with the fields -- but the DESIGN CAROUSEL FILTERS ON
         THAT DROPDOWN. Banners' templates are tagged "Custom Size" or "2m x 1m", never A1, so the
         snap silently changed which templates the customer was offered, and an A5 sign among them
         opened PitchPrint at a 21 x 14.8 cm canvas for a 2-metre banner.

         Restored with no events dispatched: Apippa priced from the width and height, not from this
         select, so the price stays at the seeded preset's. Skipped if the customer has already
         touched the calculator (the flag is gone by then) or has changed the size themselves. */
      setTimeout(function () {
        if (!document.documentElement.hasAttribute('data-c4-size-seeded')) return;
        if (sizeEl.value !== sizeWas) sizeEl.value = sizeWas;
      }, 400);
    }, 500);
  }

  /* isTrusted separates a real click or keystroke from the synthetic events the seed above (and
     the size sync, and the cart restore) dispatch. Capture phase so it runs whatever else listens. */
  if (form) {
    ['input', 'change'].forEach(function (evt) {
      document.addEventListener(evt, function (e) {
        if (!e.isTrusted || !e.target || !e.target.closest) return;
        if (!e.target.closest('#calculator')) return;
        document.documentElement.removeAttribute('data-c4-size-seeded');
      }, true);
    });
  }

  /* ---- 1b. calculator restore on the cart -> "Edit design" round trip ----------------------
     Cathy, 14 Sep 2026: "going from the cart back to the design doesn't carry the appipa
     details. this means after editing the design, adding back to cart will be wrong."

     THE CART LINE IS THE SOURCE OF TRUTH, NOT A SAVED COPY. An earlier build kept the
     calculator's values in localStorage (calculatorPersistence). That was removed for two good
     reasons: live neutralises it with a Storage.prototype blocker, and on the draft a restored
     DERIVED field froze m2 at 1 and every price with it. The line the customer is editing
     already carries Size, Width (mm), Height (mm) and Quantity as line-item properties -- the
     very numbers that produced the price they saw -- so those are read back instead of keeping a
     second copy that can drift.

     WHAT IS NOT RESTORED, AND WHY. Anything marked .lookup-element-with-formula (m2, Input2) is
     Apippa's own computed output; writing to those is exactly what froze the price last time.
     The variant carrier (#variantIdCustom) and the price token (#cpcPriceToken) are Apippa's
     too, and a stale variant id is a wrong price by definition. Only fields the CUSTOMER filled
     in are written back, and Apippa recomputes the rest -- which is the only way the price on
     the new line can be trusted.

     Note the cart line never carries m2 or Input2 (confirmed against real orders), so "restore
     what the line has" and "never restore a derived field" agree with each other. The class
     check is kept anyway: it is the guard, not an assumption about Apippa's output.

     ONE FETCH, ONLY ON THE ROUND TRIP. Read the storm note at the top of this file first. This
     runs only when ?pp_project= is present and issues exactly one /cart.js read, ever.

     Verified 14 Sep 2026: a line of 2000 x 1000mm, qty 2, priced $228 came back through the
     round trip with all four fields restored, m2 recomputed by Apippa to 8, and the price again
     $228.
  ------------------------------------------------------------------------------------------- */
  if (form && (EDIT_PROJECT || EDIT_LINE)) {
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var line = null;
        /* The LINE KEY is exact; the project id is not. Two cart lines can carry the same project
           after a re-edit, so matching on the project alone restores whichever line happens to come
           first -- clicking line three brought back line one's size and price (Cathy, 14 Sep 2026).
           The project match stays as a fallback for links minted before pp_line existed, and the
           key alone is enough for an upload-only line that has no design at all. */
        if (EDIT_LINE) {
          (cart.items || []).forEach(function (item) {
            if (!line && item.key === EDIT_LINE) line = item;
          });
        }
        if (!line && EDIT_PROJECT) {
          (cart.items || []).forEach(function (item) {
            var p = item.properties || {};
            if (!line && p._pitchprint && p._pitchprint === EDIT_PROJECT) line = item;
          });
        }
        if (!line) return;

        /* Apippa mounts late and re-renders its own fields after init, so one write is not
           enough. Wait for the fields to exist, write once, then write twice more on a short
           schedule. Each pass only touches a field whose value actually differs, so the later
           passes are no-ops unless Apippa really did reset something. */
        var tries = 0;
        var timer = setInterval(function () {
          if (applyLine(line.properties)) {
            clearInterval(timer);
            setTimeout(function () { applyLine(line.properties); }, 1500);
            setTimeout(function () { applyLine(line.properties); }, 4000);
          } else if (++tries > 60) {
            clearInterval(timer); /* ~30s and no calculator: leave the page alone */
          }
        }, 500);
      })
      .catch(function () {});
  }

  function restorableFields() {
    var calc = document.getElementById('calculator');
    if (!calc) return [];
    var all = [].slice.call(calc.querySelectorAll('.calc-prop'));
    return all.filter(function (el) {
      if (el.classList.contains('lookup-element-with-formula')) return false;
      if (el.id === 'variantIdCustom' || el.id === 'cpcPriceToken') return false;
      return true;
    });
  }

  function propertyName(el) {
    var m = (el.name || '').match(/^properties\[(.+)\]$/);
    return m ? m[1] : '';
  }

  function fireInput(el) {
    el.dispatchEvent(new Event('input', { bubbles: true }));
    el.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /* Returns false when the calculator is not on the page yet, so the caller keeps waiting. */
  function applyLine(props) {
    var fields = restorableFields();
    if (!fields.length) return false;

    /* Selects first. Choosing a preset size auto-fills width and height (see the size sync in
       theme.liquid), so the line's own width and height have to be written afterwards to win. */
    var ordered = fields.filter(function (el) { return el.tagName === 'SELECT'; })
      .concat(fields.filter(function (el) { return el.tagName !== 'SELECT'; }));

    ordered.forEach(function (el) {
      var key = propertyName(el);
      if (!key || !(key in props)) return;
      var want = String(props[key]);

      if (el.type === 'radio' || el.type === 'checkbox') {
        var on = el.value === want;
        if (el.checked !== on) { el.checked = on; fireInput(el); }
        return;
      }

      if (el.tagName === 'SELECT') {
        /* A size the calculator no longer offers must not be forced in: leaving Apippa's own
           default and letting width/height drive the price is the safe failure. */
        var found = false;
        for (var i = 0; i < el.options.length; i++) {
          if (el.options[i].value === want) { found = true; break; }
        }
        if (!found) return;
        if (el.value !== want) { el.value = want; fireInput(el); }
        return;
      }

      if (el.value !== want) { el.value = want; fireInput(el); }
    });

    return true;
  }

  /* ---- 2 + 3. cart presentation ----------------------------------------------------------- */
  var CART_SEL = '.cart-items, cart-items-component, cart-drawer-component, #main-cart-items, [id*="cart-drawer"]';
  var onCart = !!document.querySelector(CART_SEL);
  if (!onCart) return;

  var CARRIER = /Default_cpc_/i;
  var LINE_SEL = '[data-line-item-key], [data-cart-item-key], [data-key], .cart-item';
  var DEBOUNCE_MS = 350;
  var MIN_FETCH_MS = 1500;

  /* The drawer exists in the layout on every page, so "a cart container is present" is not the
     same question as "there is anything to do". This is the one that matters. */
  function hasCartLines() {
    var roots = document.querySelectorAll(CART_SEL);
    for (var i = 0; i < roots.length; i++) {
      if (roots[i].querySelector(LINE_SEL)) return true;
    }
    return false;
  }

  function tidy(root) {
    var scope = root || document;

    /* Hide any element whose entire text is a carrier variant title. Checking the deepest node
       keeps a parent that merely contains it (the whole line) visible. */
    var all = scope.querySelectorAll('span, p, div, dd, li, small');
    for (var i = 0; i < all.length; i++) {
      var el = all[i];
      if (el.children.length) continue;
      var txt = (el.textContent || '').trim();
      if (!txt || !CARRIER.test(txt)) continue;
      if (el.hasAttribute('data-c4-hid')) continue;
      el.setAttribute('data-c4-hid', '');
      el.style.display = 'none';
      /* A label sitting immediately before it ("Variant:") would otherwise dangle. */
      var prev = el.previousElementSibling;
      if (prev && !prev.children.length && /^[a-z ]{0,14}:?$/i.test((prev.textContent || '').trim())) {
        prev.style.display = 'none';
      }
    }
  }

  var lastFetchAt = 0;
  var fetchInFlight = false;

  function lockQuantities() {
    /* Guard 1: nothing on screen to lock -- do not touch the network. */
    if (!hasCartLines()) return;
    /* Guard 2: never two reads inside MIN_FETCH_MS, whatever is calling us. */
    var now = Date.now();
    if (fetchInFlight || now - lastFetchAt < MIN_FETCH_MS) return;
    fetchInFlight = true;
    lastFetchAt = now;

    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        (cart.items || []).forEach(function (item) {
          var props = item.properties || {};
          if (!('_cpc_custom_cart_item' in props)) return;
          var row = rowFor(item.key);
          if (!row) return;
          /* HIDE THE STEPPER, NEVER ITS CONTAINER. An earlier selector included
             [class*="quantity"], which matched the whole <td class="cart-items__quantity"> --
             and the Remove (bin) button lives inside it, so locking the quantity silently took
             Remove away with it (Cathy, 14 Sep 2026). Match the selector itself only.

             Nothing replaces the stepper: see note 3 at the top of this file. */
          var stepper = row.querySelector('.quantity-selector, cart-quantity-selector, quantity-selector');
          if (!stepper || stepper.hasAttribute('data-c4-qty-locked')) return;
          stepper.setAttribute('data-c4-qty-locked', '');
          stepper.style.display = 'none';
        });
      })
      .catch(function () {})
      .then(function () { fetchInFlight = false; });
  }

  function rowFor(key) {
    var direct = document.querySelector('[data-line-item-key="' + key + '"], [data-cart-item-key="' + key + '"], [data-key="' + key + '"]');
    if (direct) return direct.closest('.cart-item, tr, li') || direct;
    var link = document.querySelector('a[href*="' + key + '"]');
    return link ? link.closest('.cart-item, tr, li') : null;
  }

  function run() { tidy(); lockQuantities(); }

  /* Coalesce a burst of mutations into one trailing pass. Without this, every DOM change on the
     page bought a full tidy() sweep and a cart read -- see the note at the top of this file. */
  var runTimer = null;
  function schedule() {
    if (runTimer) return;
    runTimer = setTimeout(function () { runTimer = null; run(); }, DEBOUNCE_MS);
  }

  run();
  /* The cart re-renders through the Section Rendering API on quantity/remove; re-apply then. */
  var mo = new MutationObserver(schedule);
  mo.observe(document.body, { childList: true, subtree: true });
  document.addEventListener('visibilitychange', function () { if (!document.hidden) schedule(); });
})();
